// web/redux/slices/masterWebPageSettingsSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { resetStore } from "./resetStore";
import { getServerUrl, getAuthHeaders } from "../../Utils/serverSelector";

// ===== utils (рядом с deepMerge) =====
function normalizeSettings(serverSettings = {}) {
  // Если сервер прислал вложенную "settings" — поднимаем её на верх
  if (serverSettings && typeof serverSettings === "object" && serverSettings.settings) {
    const merged = deepMerge(serverSettings, serverSettings.settings); // inner перекрывает outer
    const { settings, ...rest } = merged; // убираем вложенное поле
    return rest;
  }
  return serverSettings;
}

// ===== DEFAULTS =====
const DEFAULTS = {
  cover: { photoUrl: null },
  avatar: { photoUrl: null, prefer: "auto" },
  theme: {
    primary: "#26264F",
    text: "#0b0f1a",
    headerMode: "auto", // "auto" | "gradient" | "photo"
    gradientTop: "#a5d7d6",
    gradientBottom: "#c3e0a5",
    background: null,
  },
  booking: {
    finalMessage: { ru: "", he: "", en: "" },
    prepaymentNewClients: {
      enabled: false,
      type: "fixed",
      amount: "",
    },
    prepaymentTerms: {
      enabled: false,
      filePath: null,
      originalName: null,
      mimeType: null,
      size: null,
      updatedAt: null,
      merchantConfirmed: false,
      merchantConfirmedAt: null,
    },
    prepaymentReceipt: {
      enabled: false,
    },
  },
  infoBanner: { text: { ru: "", he: "", en: "" } },
  ui: {
    blocks: { tabs: true, pickTitle: true, categories: true, serviceCard: true, banner: true },
    menu: { certs: true, shop: true },
  },
};

// ===== utils =====
const isObj = (x) => x && typeof x === "object" && !Array.isArray(x);
function deepMerge(a, b) {
  if (Array.isArray(a) || Array.isArray(b)) return b ?? a;
  if (isObj(a) && isObj(b)) {
    const out = { ...a };
    for (const k of Object.keys(b)) out[k] = deepMerge(a?.[k], b[k]);
    return out;
  }
  return b ?? a;
}
function setByPathImmutable(obj, path, value) {
  const keys = String(path).split(".");
  const out = Array.isArray(obj) ? [...obj] : { ...obj };
  let curOut = out,
    curSrc = obj;
  for (let i = 0; i < keys.length; i++) {
    const k = keys[i];
    const last = i === keys.length - 1;
    if (last) {
      curOut[k] = value;
    } else {
      const nextSrc = curSrc?.[k];
      const next = Array.isArray(nextSrc) ? [...nextSrc] : { ...(nextSrc || {}) };
      curOut[k] = next;
      curOut = next;
      curSrc = nextSrc || {};
    }
  }
  return out;
}
const noCache = (url) => (url ? `${url}${url.includes("?") ? "&" : "?"}t=${Date.now()}` : url);
const toAbsolute = (filePath) => {
  if (!filePath) return null;
  if (/^https?:\/\//i.test(filePath)) return filePath;
  const base = (getServerUrl() || "").replace(/\/$/, "");
  const path = String(filePath).replace(/^\//, "");
  return `${base}/${path}`;
};

// ===== auth helpers =====
const stripContentType = (h = {}) => {
  const copy = { ...h };

  if (copy["Content-Type"]) delete copy["Content-Type"];
  if (copy["content-type"]) delete copy["content-type"];

  if (copy.common) {
    delete copy.common["Content-Type"];
    delete copy.common["content-type"];
  }
  if (copy.post) {
    delete copy.post["Content-Type"];
    delete copy.post["content-type"];
  }

  return copy;
};

const authJsonConfig = async () => {
  const headers = await getAuthHeaders();
  return {
    headers,
    withCredentials: true,
    validateStatus: () => true, // сами проверяем status
  };
};

const authJsonConfigWithContentType = async () => {
  const baseCfg = await authJsonConfig();
  return {
    ...baseCfg,
    headers: {
      ...baseCfg.headers,
      "Content-Type": "application/json",
    },
  };
};

const authMultipartConfig = async () => {
  const headers = stripContentType(await getAuthHeaders());
  return {
    headers,
    withCredentials: true,
    validateStatus: () => true,
  };
};

// ===== Thunks: SETTINGS CRUD =====
// GET /masterWebPageSettings/:uid/webpage-settings -> { settings }
export const fetchMasterWebPageSettings = createAsyncThunk(
  "masterWebPageSettings/fetch",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const cfg = await authJsonConfig();
      const res = await axios.get(
        `${getServerUrl()}/masterWebPageSettings/${uid}/webpage-settings`,
        cfg
      );

      if (res.status === 404) return DEFAULTS; // ✅ 404 = просто дефолты

      if (res.status !== 200) {
        throw new Error(
          res.data?.message ||
          `Failed to fetch master webpage settings (HTTP ${res.status})`
        );
      }

      const serverSettingsRaw = res.data?.settings || {};
      const serverSettings = normalizeSettings(serverSettingsRaw);
      return deepMerge(DEFAULTS, serverSettings);
    } catch (e) {
      return rejectWithValue(e?.response?.data || e.message);
    }
  }
);

// PUT /masterWebPageSettings/:uid/webpage-settings  body:{ settings }
export const saveMasterWebPageSettings = createAsyncThunk(
  "masterWebPageSettings/save",
  async ({ uid, settings }, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const cfg = await authJsonConfigWithContentType();
      const res = await axios.put(
        `${getServerUrl()}/masterWebPageSettings/${uid}/webpage-settings`,
        { settings },
        cfg
      );

      if (res.status === 404) return DEFAULTS;

      if (res.status !== 200 && res.status !== 201) {
        throw new Error(
          res.data?.message ||
          `Failed to save master webpage settings (HTTP ${res.status})`
        );
      }

      const saved = res.data?.settings || settings || {};
      return deepMerge(DEFAULTS, saved);
    } catch (e) {
      return rejectWithValue(e?.response?.data || e.message);
    }
  }
);

// PATCH /masterWebPageSettings/:uid/webpage-settings  body:{ patch }
export const patchMasterWebPageSettings = createAsyncThunk(
  "masterWebPageSettings/patch",
  async ({ uid, patch }, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const cfg = await authJsonConfigWithContentType();
      const res = await axios.patch(
        `${getServerUrl()}/masterWebPageSettings/${uid}/webpage-settings`,
        { patch },
        cfg
      );

      if (res.status === 404) return DEFAULTS;

      if (res.status !== 200) {
        throw new Error(
          res.data?.message ||
          `Failed to patch master webpage settings (HTTP ${res.status})`
        );
      }

      const serverSettings = res.data?.settings || {};
      return deepMerge(DEFAULTS, serverSettings);
    } catch (e) {
      return rejectWithValue(e?.response?.data || e.message);
    }
  }
);

// ===== Thunks: COVER =====
// POST /masterWebPageSettings/:uid/cover  (form-data: file)
export const uploadCoverImage = createAsyncThunk(
  "masterWebPageSettings/uploadCoverImage",
  async ({ uid, file }, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");
      if (!file) throw new Error("Файл не передан");

      const form = new FormData();
      // поле именно "file": { blob|File, name, type }
      form.append("file", file);

      const cfg = await authMultipartConfig();
      const res = await axios.post(
        `${getServerUrl()}/masterWebPageSettings/${uid}/cover`,
        form,
        cfg
      );

      if (res.status !== 200 && res.status !== 201) {
        throw new Error(
          res.data?.message ||
          `Failed to upload cover image (HTTP ${res.status})`
        );
      }

      // сервер возвращает { filePath }
      const filePath = res.data?.filePath;
      const url = toAbsolute(filePath);
      return { url: noCache(url) };
    } catch (e) {
      if (e?.response?.status === 404) return DEFAULTS; // как было в старом коде
      return rejectWithValue(e?.response?.data || e.message);
    }
  }
);

// GET /masterWebPageSettings/:uid/cover -> { filePath }
export const fetchCoverImage = createAsyncThunk(
  "masterWebPageSettings/fetchCoverImage",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const cfg = await authJsonConfig();
      const res = await axios.get(
        `${getServerUrl()}/masterWebPageSettings/${uid}/cover`,
        cfg
      );

      if (res.status === 404) return null;

      if (res.status !== 200) {
        throw new Error(
          res.data?.message ||
          `Failed to fetch cover image (HTTP ${res.status})`
        );
      }

      const filePath = res.data?.filePath;
      const url = toAbsolute(filePath);
      return noCache(url) || null;
    } catch (e) {
      return rejectWithValue(e?.response?.data || e.message);
    }
  }
);

// DELETE /masterWebPageSettings/:uid/cover
export const deleteCoverImage = createAsyncThunk(
  "masterWebPageSettings/deleteCoverImage",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const cfg = await authJsonConfig();
      const res = await axios.delete(
        `${getServerUrl()}/masterWebPageSettings/${uid}/cover`,
        cfg
      );

      if (res.status === 404) return DEFAULTS;

      if (res.status !== 200) {
        throw new Error(
          res.data?.message ||
          `Failed to delete cover image (HTTP ${res.status})`
        );
      }

      return true;
    } catch (e) {
      return rejectWithValue(e?.response?.data || e.message);
    }
  }
);

// ===== Thunks: PREPAYMENT TERMS =====
// POST /masterWebPageSettings/:uid/prepayment-terms (form-data: file)
export const uploadPrepaymentTerms = createAsyncThunk(
  "masterWebPageSettings/uploadPrepaymentTerms",
  async ({ uid, file }, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");
      if (!file) throw new Error("Файл не передан");

      const isPdf =
        file.type === "application/pdf" ||
        file.name?.toLowerCase().endsWith(".pdf");

      if (!isPdf) throw new Error("Разрешены только PDF-файлы");

      const form = new FormData();
      form.append("file", file);

      const cfg = await authMultipartConfig();
      const res = await axios.post(
        `${getServerUrl()}/masterWebPageSettings/${uid}/prepayment-terms`,
        form,
        cfg
      );

      if (res.status !== 200 && res.status !== 201) {
        throw new Error(
          res.data?.message ||
          res.data?.error ||
          `Failed to upload prepayment terms (HTTP ${res.status})`
        );
      }

      const responseSettings = res.data?.settings
        ? normalizeSettings(res.data.settings)
        : null;

      const terms =
        res.data?.prepaymentTerms ||
        responseSettings?.booking?.prepaymentTerms ||
        {
          enabled: true,
          filePath: res.data?.filePath || null,
          originalName: res.data?.originalName || file.name || null,
          mimeType: res.data?.mimeType || file.type || "application/pdf",
          size: res.data?.size ?? file.size ?? null,
          updatedAt: res.data?.updatedAt || new Date().toISOString(),
          merchantConfirmed: false,
          merchantConfirmedAt: null,
        };

      return {
        settings: responseSettings
          ? deepMerge(DEFAULTS, responseSettings)
          : null,
        terms: {
          ...terms,
          enabled: true,
          merchantConfirmed: false,
          merchantConfirmedAt: null,
        },
      };
    } catch (e) {
      return rejectWithValue(
        e?.response?.data || e.message || "Failed to upload prepayment terms"
      );
    }
  }
);

// DELETE /masterWebPageSettings/:uid/prepayment-terms
export const deletePrepaymentTerms = createAsyncThunk(
  "masterWebPageSettings/deletePrepaymentTerms",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const cfg = await authJsonConfig();
      const res = await axios.delete(
        `${getServerUrl()}/masterWebPageSettings/${uid}/prepayment-terms`,
        cfg
      );

      if (res.status !== 200 && res.status !== 204 && res.status !== 404) {
        throw new Error(
          res.data?.message ||
          res.data?.error ||
          `Failed to delete prepayment terms (HTTP ${res.status})`
        );
      }

      const responseSettings = res.data?.settings
        ? normalizeSettings(res.data.settings)
        : null;

      return {
        settings: responseSettings
          ? deepMerge(DEFAULTS, responseSettings)
          : null,
      };
    } catch (e) {
      return rejectWithValue(
        e?.response?.data || e.message || "Failed to delete prepayment terms"
      );
    }
  }
);

// ===== PUBLIC Thunks (без авторизации) =====

// GET /public/:uid/master-webpage-settings -> { settings }
export const fetchPublicMasterWebPageSettings = createAsyncThunk(
  "masterWebPageSettings/fetchPublic",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");
      const base = (getServerUrl() || "").replace(/\/$/, "");

      const res = await axios.get(`${base}/public/${uid}/master-webpage-settings`);
      const raw = res.data?.settings || res.data || {};
      const serverSettings = normalizeSettings(raw);

      return deepMerge(DEFAULTS, serverSettings);
    } catch (e) {
      if (e?.response?.status === 404) return DEFAULTS;
      return rejectWithValue(
        e?.response?.data || e.message || "Failed to fetch public webpage settings"
      );
    }
  }
);

// GET /public/:uid/cover -> { filePath }
export const fetchPublicCoverImage = createAsyncThunk(
  "masterWebPageSettings/fetchPublicCoverImage",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");
      const base = (getServerUrl() || "").replace(/\/$/, "");

      const res = await axios.get(`${base}/public/${uid}/cover`);
      const filePath = res.data?.filePath;
      const url = toAbsolute(filePath);
      return noCache(url) || null;
    } catch (e) {
      if (e?.response?.status === 404) return null;
      return rejectWithValue(
        e?.response?.data || e.message || "Failed to fetch public cover"
      );
    }
  }
);

// ===== Slice =====
const initialState = {
  settings: DEFAULTS,
  status: "idle",
  error: null,
  lastSavedAt: null,
  hydrated: false,
};

const slice = createSlice({
  name: "masterWebPageSettings",
  initialState,
  reducers: {
    replaceAllSettings(state, action) {
      state.settings = deepMerge(DEFAULTS, action.payload || {});
    },
    setField(state, action) {
      const { path, value } = action.payload || {};
      state.settings = setByPathImmutable(state.settings, path, value);
    },
    mergeSettings(state, action) {
      state.settings = deepMerge(state.settings, action.payload || {});
    },
  },
  extraReducers: (builder) => {
    builder
      // fetch
      .addCase(fetchMasterWebPageSettings.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchMasterWebPageSettings.fulfilled, (state, action) => {
        state.status = "idle";
        state.settings = deepMerge(DEFAULTS, action.payload || {});
        state.error = null;
      })
      .addCase(fetchMasterWebPageSettings.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error?.message;
      })

      // save
      .addCase(saveMasterWebPageSettings.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(saveMasterWebPageSettings.fulfilled, (state, action) => {
        state.status = "idle";
        state.settings = deepMerge(DEFAULTS, action.payload || {});
        state.lastSavedAt = Date.now();
        state.error = null;
      })
      .addCase(saveMasterWebPageSettings.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error?.message;
      })

      // patch
      .addCase(patchMasterWebPageSettings.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(patchMasterWebPageSettings.fulfilled, (state, action) => {
        state.status = "idle";
        state.settings = deepMerge(DEFAULTS, action.payload || {});
        state.lastSavedAt = Date.now();
        state.error = null;
      })
      .addCase(patchMasterWebPageSettings.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error?.message;
      })

      // cover upload
      .addCase(uploadCoverImage.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(uploadCoverImage.fulfilled, (state, action) => {
        state.status = "idle";
        const next = { ...state.settings };
        const url = action.payload?.url || null;
        next.cover = { ...(next.cover || {}), photoUrl: url };
        state.settings = next;
        state.lastSavedAt = Date.now();
      })
      .addCase(uploadCoverImage.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error?.message;
      })

      // cover fetch
      .addCase(fetchCoverImage.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchCoverImage.fulfilled, (state, action) => {
        state.status = "idle";
        const next = { ...state.settings };
        next.cover = { ...(next.cover || {}), photoUrl: action.payload || null };
        state.settings = next;
      })
      .addCase(fetchCoverImage.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error?.message;
      })

      // cover delete
      .addCase(deleteCoverImage.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(deleteCoverImage.fulfilled, (state) => {
        state.status = "idle";
        const next = { ...state.settings };
        next.cover = { ...(next.cover || {}), photoUrl: null };
        state.settings = next;
        state.lastSavedAt = Date.now();
      })
      .addCase(deleteCoverImage.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error?.message;
      })

      // prepayment terms upload
      .addCase(uploadPrepaymentTerms.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(uploadPrepaymentTerms.fulfilled, (state, action) => {
        state.status = "idle";

        if (action.payload?.settings) {
          state.settings = deepMerge(DEFAULTS, action.payload.settings);
        }

        const currentBooking = state.settings?.booking || {};
        const currentTerms = currentBooking.prepaymentTerms || {};

        state.settings = {
          ...state.settings,
          booking: {
            ...currentBooking,
            prepaymentTerms: {
              ...currentTerms,
              ...(action.payload?.terms || {}),
              enabled: true,
              merchantConfirmed: false,
              merchantConfirmedAt: null,
            },
            prepaymentNewClients: {
              ...(currentBooking.prepaymentNewClients || {}),
              enabled: false,
            },
          },
        };

        state.lastSavedAt = Date.now();
        state.error = null;
      })
      .addCase(uploadPrepaymentTerms.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error?.message;
      })

      // prepayment terms delete
      .addCase(deletePrepaymentTerms.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(deletePrepaymentTerms.fulfilled, (state, action) => {
        state.status = "idle";

        if (action.payload?.settings) {
          state.settings = deepMerge(DEFAULTS, action.payload.settings);
        }

        const currentBooking = state.settings?.booking || {};

        state.settings = {
          ...state.settings,
          booking: {
            ...currentBooking,
            prepaymentTerms: {
              enabled: false,
              filePath: null,
              originalName: null,
              mimeType: null,
              size: null,
              updatedAt: null,
              merchantConfirmed: false,
              merchantConfirmedAt: null,
            },
            prepaymentNewClients: {
              ...(currentBooking.prepaymentNewClients || {}),
              enabled: false,
            },
            prepaymentReceipt: {
              ...(currentBooking.prepaymentReceipt || {}),
              enabled: false,
            },
          },
        };

        state.lastSavedAt = Date.now();
        state.error = null;
      })
      .addCase(deletePrepaymentTerms.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error?.message;
      })

      // ===== PUBLIC: fetch settings
      .addCase(fetchPublicMasterWebPageSettings.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchPublicMasterWebPageSettings.fulfilled, (state, action) => {
        state.status = "idle";
        state.settings = deepMerge(DEFAULTS, action.payload || {});
        state.error = null;
        state.hydrated = true;
      })
      .addCase(fetchPublicMasterWebPageSettings.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error?.message;
      })

      // ===== PUBLIC: fetch cover
      .addCase(fetchPublicCoverImage.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchPublicCoverImage.fulfilled, (state, action) => {
        state.status = "idle";
        const next = { ...state.settings };
        next.cover = { ...(next.cover || {}), photoUrl: action.payload || null };
        state.settings = next;
        state.hydrated = true;
      })
      .addCase(fetchPublicCoverImage.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error?.message;
      })

      // reset
      .addCase(resetStore, () => initialState);
  },
});

export const { replaceAllSettings, setField, mergeSettings } = slice.actions;
export default slice.reducer;

// selectors
export const selectMasterWebPageSettings = (s) =>
  s.masterWebPageSettings?.settings || DEFAULTS;
export const selectMasterWebPageStatus = (s) =>
  s.masterWebPageSettings?.status || "idle";
export const selectCoverUrl = (s) =>
  s.masterWebPageSettings?.settings?.cover?.photoUrl || null;
export const selectTheme = (s) =>
  s.masterWebPageSettings?.settings?.theme || DEFAULTS.theme;
export const selectMWPSHydrated = (s) => !!s.masterWebPageSettings?.hydrated;